import { get } from "@/lib/api";

/**
 * Basemaps for the operational map (Mapbox GL). Two moods only: a very light, de-cluttered vector map where projects,
 * depots and transports are the protagonists – and satellite for reading the actual site. Style URLs and the public
 * `pk.` token come from the authenticated backend (`/api/map/config`). Riggdesign/Tomt-modus keeps its raster proxy.
 */
export const MAP_LAYERS = [
  { id: "light", label: "Kart", attribution: "© Mapbox © OpenStreetMap" },
  { id: "satellite", label: "Satellitt", attribution: "© Mapbox © Maxar © OpenStreetMap" },
];
export const DEFAULT_LAYER = "light";
export const LAYER_STORAGE_KEY = "si_map_layer_v2";

export const layerById = (id) => MAP_LAYERS.find((l) => l.id === id) || MAP_LAYERS[0];

let configPromise = null;
/** Cached client map configuration: { provider, token, styles: { light, satellite }, home_bounds }. */
export const loadMapConfig = () => {
  if (!configPromise) {
    configPromise = get("/map/config").catch((e) => {
      configPromise = null;
      throw e;
    });
  }
  return configPromise;
};

export const readStoredLayer = () => {
  try {
    const v = localStorage.getItem(LAYER_STORAGE_KEY);
    return MAP_LAYERS.some((l) => l.id === v) ? v : DEFAULT_LAYER;
  } catch {
    return DEFAULT_LAYER;
  }
};
export const storeLayer = (id) => {
  try {
    localStorage.setItem(LAYER_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
};

/** Vestland / Sunnhordland operating area – used before data arrives and as a sane fallback. */
export const HOME_BOUNDS = [
  [4.6, 59.2],
  [6.9, 61.0],
];

/* ------------------------------------------------------------------ basemap styling ---- */

/**
 * Mapbox styles label places in English (`name_en`) by default. Norwegian place names are the local `name`, so every
 * symbol layer that reads `name_en` is pointed at `name` instead (Norge, Bergen, Stord …).
 */
const localizeLabels = (map, id) => {
  try {
    const tf = map.getLayoutProperty(id, "text-field");
    if (!tf) return;
    const raw = JSON.stringify(tf);
    if (!raw.includes("name_en")) return;
    map.setLayoutProperty(id, "text-field", JSON.parse(raw.replaceAll('"name_en"', '"name"')));
  } catch {
    /* layer without text or non-JSON expression – leave as is */
  }
};

const HIDE = /(^|-)poi|transit|airport|ferry-aerialway-label|road-number-shield|road-exit-shield|golf|pitch|housenum|building-number|path-pedestrian-label|natural-point-label|water-point-label|water-line-label|waterway-label|settlement-subdivision-label|admin-1-boundary-bg|road-intersection|turning-feature|road-oneway-arrow/;
const ROAD = /^(road|bridge|tunnel)/;

/**
 * Turn Mapbox Light into the near-monochrome canvas the operations map wants: no POIs or transit, muted roads, softer
 * water, quiet labels and a whisper of terrain. Everything is best-effort – a missing layer is never an error.
 */
export const applyCleanStyle = (map) => {
  const style = map.getStyle();
  if (!style?.layers) return;
  const safe = (fn) => {
    try {
      fn();
    } catch {
      /* layer/property not present in this style version */
    }
  };
  for (const l of style.layers) {
    const id = l.id;
    if (HIDE.test(id)) {
      safe(() => map.setLayoutProperty(id, "visibility", "none"));
      continue;
    }
    if (l.type === "line" && ROAD.test(id)) {
      safe(() => map.setPaintProperty(id, "line-opacity", /motorway|trunk|primary/.test(id) ? 0.7 : 0.5));
    }
    if (l.type === "symbol") {
      localizeLabels(map, id);
      if (/label/.test(id)) {
        safe(() => map.setPaintProperty(id, "text-color", /settlement-major/.test(id) ? "#4b5563" : "#7b8494"));
        safe(() => map.setPaintProperty(id, "text-halo-color", "rgba(255,255,255,0.92)"));
        safe(() => map.setPaintProperty(id, "text-halo-width", 1.2));
      }
    }
  }
  safe(() => map.setPaintProperty("land", "background-color", "#f4f5f7"));
  safe(() => map.setPaintProperty("water", "fill-color", "#d7dfe8"));
  safe(() => map.setPaintProperty("landuse", "fill-opacity", 0.25));
  safe(() => map.setPaintProperty("national-park", "fill-opacity", 0.12));
  safe(() => map.setPaintProperty("landcover", "fill-opacity", 0.18));
  safe(() => map.setPaintProperty("building", "fill-opacity", 0.35));
  // subtle terrain – relief without contour noise
  safe(() => {
    if (!map.getSource("si-dem")) map.addSource("si-dem", { type: "raster-dem", url: "mapbox://mapbox.mapbox-terrain-dem-v1", tileSize: 512, maxzoom: 14 });
    if (!map.getLayer("si-hillshade")) {
      const firstSymbol = style.layers.find((l) => l.type === "symbol")?.id;
      map.addLayer({ id: "si-hillshade", type: "hillshade", source: "si-dem", paint: { "hillshade-exaggeration": 0.22, "hillshade-shadow-color": "#c3cad3", "hillshade-highlight-color": "#ffffff", "hillshade-accent-color": "#dde2e8", "hillshade-illumination-direction": 320 } }, firstSymbol);
    }
  });
};

/** Satellite gets its labels quieted too, so the imagery reads as a photo of the site rather than a street map. */
export const applySatelliteStyle = (map) => {
  const style = map.getStyle();
  if (!style?.layers) return;
  for (const l of style.layers) {
    if (l.type === "symbol") localizeLabels(map, l.id);
    if (/(^|-)poi|transit|airport|road-number-shield|road-exit-shield|housenum|building-number|natural-point-label|water-point-label|waterway-label|settlement-subdivision-label/.test(l.id)) {
      try {
        map.setLayoutProperty(l.id, "visibility", "none");
      } catch {
        /* ignore */
      }
    }
  }
};
